import { useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Plus, Trash2, X } from "lucide-react";
import { tanggal } from "@/lib/format";

/**
 * headerFields: [{name,label,type,optionsRef,required, dependsOn}]
 * itemFields: [{name,label,type}] (material handled specially)
 */
export function DocWithItems({ title, subtitle, endpoint, testId, listColumns, headerFields, itemFields, defaultValues = {}, readOnlyNote }) {
  const { data, loading, reload } = useFetch(endpoint);
  const { data: clients } = useFetch("/clients");
  const { data: projects } = useFetch("/projects");
  const { data: contracts } = useFetch("/contracts");
  const { data: materials } = useFetch("/materials");
  const { data: pos } = useFetch("/purchase-orders");
  const refs = { clients: clients || [], projects: projects || [], contracts: contracts || [], purchase_orders: pos || [] };

  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState({});
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm({ ...defaultValues, date: new Date().toISOString().slice(0, 10) }); setItems([]); setViewing(null); setOpen(true); };
  const openView = (row) => { setViewing(row); setOpen(true); };
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addItem = () => setItems((it) => [...it, { material_id: "", ...Object.fromEntries(itemFields.map((f) => [f.name, ""])) }]);
  const setItem = (i, k, v) => setItems((its) => its.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const rmItem = (i) => setItems((its) => its.filter((_, idx) => idx !== i));

  const save = async () => {
    for (const f of headerFields) if (f.required && !form[f.name]) { toast.error(`${f.label} wajib diisi`); return; }
    if (items.length === 0) { toast.error("Tambahkan minimal satu item material"); return; }
    const payload = { ...form, items: items.map((it) => {
      const mat = (materials || []).find((m) => m.id === it.material_id) || {};
      const numItem = {};
      itemFields.forEach((f) => { numItem[f.name] = f.type === "number" ? Number(it[f.name] || 0) : it[f.name]; });
      return { ...numItem, material_id: it.material_id, material_name: mat.name, material_code: mat.code, unit: mat.unit };
    }) };
    setSaving(true);
    try {
      await api.post(endpoint, payload);
      toast.success("Data tersimpan"); setOpen(false); reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}
        action={<Button onClick={openNew} data-testid={`${testId}-add-btn`} className="bg-[#9333EA] hover:bg-[#7e22ce]"><Plus className="h-4 w-4 mr-1" /> Tambah</Button>} />
      {loading ? <div className="h-64 rounded-md bg-muted animate-pulse" /> : (
        <DataTable testId={testId} columns={listColumns} data={data || []} onRowClick={openView} />
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto scrollbar-thin">
          <SheetHeader><SheetTitle className="font-display">{viewing ? `Detail ${title}` : `Tambah ${title}`}</SheetTitle></SheetHeader>
          {viewing ? (
            <ViewDoc doc={viewing} headerFields={headerFields} itemFields={itemFields} refs={refs} readOnlyNote={readOnlyNote} />
          ) : (
            <>
              <div className="space-y-4 py-6">
                {headerFields.map((f) => (
                  <div key={f.name} className="space-y-1.5">
                    <Label className="text-sm">{f.label}{f.required && <span className="text-rose-500"> *</span>}</Label>
                    {f.type === "select" ? (
                      <Select value={form[f.name] || ""} onValueChange={(v) => setF(f.name, v)}>
                        <SelectTrigger data-testid={`${testId}-field-${f.name}`}><SelectValue placeholder={`Pilih ${f.label}`} /></SelectTrigger>
                        <SelectContent>
                          {optionsFor(f, refs, form).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input type={f.type === "date" ? "date" : "text"} data-testid={`${testId}-field-${f.name}`}
                        value={form[f.name] || ""} onChange={(e) => setF(f.name, e.target.value)} />
                    )}
                  </div>
                ))}

                <div className="border rounded-md p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Item Material</Label>
                    <Button variant="outline" size="sm" onClick={addItem} data-testid={`${testId}-add-item`}><Plus className="h-3 w-3 mr-1" /> Item</Button>
                  </div>
                  {items.length === 0 && <p className="text-xs text-muted-foreground">Belum ada item.</p>}
                  {items.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end border-b pb-3">
                      <div className="col-span-5 space-y-1">
                        <Label className="text-[11px]">Material</Label>
                        <Select value={it.material_id} onValueChange={(v) => setItem(i, "material_id", v)}>
                          <SelectTrigger data-testid={`${testId}-item-${i}-material`} className="h-9"><SelectValue placeholder="Material" /></SelectTrigger>
                          <SelectContent>{(materials || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {itemFields.map((f) => (
                        <div key={f.name} className={`col-span-3 space-y-1`}>
                          <Label className="text-[11px]">{f.label}</Label>
                          <Input type="number" className="h-9" data-testid={`${testId}-item-${i}-${f.name}`} value={it[f.name] || ""} onChange={(e) => setItem(i, f.name, e.target.value)} />
                        </div>
                      ))}
                      <div className="col-span-1">
                        <Button variant="ghost" size="icon" onClick={() => rmItem(i)}><X className="h-4 w-4 text-rose-500" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <SheetFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
                <Button onClick={save} disabled={saving} data-testid={`${testId}-save-btn`} className="bg-[#9333EA] hover:bg-[#7e22ce]">{saving ? "Menyimpan..." : "Simpan"}</Button>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function optionsFor(f, refs, form) {
  if (f.optionsRef) {
    let list = refs[f.optionsRef] || [];
    if (f.filterBy && form[f.filterBy]) list = list.filter((x) => x[f.filterBy] === form[f.filterBy]);
    return list.map((o) => ({ value: o.id, label: o.number || `${o.code ? o.code + " · " : ""}${o.name}` }));
  }
  return (f.options || []).map((o) => ({ value: String(o.value), label: o.label }));
}

function ViewDoc({ doc, itemFields, refs, readOnlyNote }) {
  const name = (ref, id) => (refs[ref] || []).find((x) => x.id === id)?.name || (refs[ref] || []).find((x) => x.id === id)?.number || id || "-";
  return (
    <div className="py-6 space-y-4">
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Info label="Nomor" value={doc.number} />
        <Info label="Tanggal" value={tanggal(doc.date)} />
        <Info label="Klien" value={name("clients", doc.client_id)} />
        <Info label="Proyek" value={name("projects", doc.project_id)} />
        <Info label="Status" value={<StatusBadge status={doc.status} />} />
      </div>
      {readOnlyNote && <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-3">{readOnlyNote}</p>}
      <div className="rounded-md border overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-muted/40 border-b">
            <th className="text-left px-3 py-2 text-xs">Material</th>
            {itemFields.map((f) => <th key={f.name} className="text-right px-3 py-2 text-xs">{f.label}</th>)}
          </tr></thead>
          <tbody>
            {(doc.items || []).map((it, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="px-3 py-2">{it.material_name}</td>
                {itemFields.map((f) => <td key={f.name} className="px-3 py-2 text-right tabular-nums">{it[f.name] ?? 0}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return <div><div className="text-xs text-muted-foreground">{label}</div><div className="font-medium">{value}</div></div>;
}
